// components/Recordings/RecordingPlayerPage.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, Play, Pause, Tag, FileText, Download, Eye, CheckCircle2 } from "lucide-react";
import { BackButton, AppButton } from "../ui";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import RecordingListItem from "./RecordingListItem";
import VideoComments from "../Videos/VideoComments";
import ContentWatermark from "../Common/ContentWatermark";
import CommonToaster from "../Common/CommonToaster";
import { startSession } from "../../utils/sessionTracker";
import useVideoProtection from "../../hooks/useVideoProtection";
import { usePlan } from "../../contexts/PlanContext";
import { getCurrentUser } from "../../utils/auth";
import { isDesktopApp } from "../../utils/desktopApp";
import DesktopAppRequired from "../Common/DesktopAppRequired";
import "../../styles/VideoProtection.scss";
import "./Recordings.scss";

// ─── Inline Video Player ───
const VideoPlayer = ({ url, loading, showVideo, currentRec, onUrlExpired }) => {
  const [hovered, setHovered] = useState(false);
  const [playing, setPlaying] = useState(true);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useVideoProtection({
    containerRef,
    onPause: useCallback(() => { videoRef.current?.pause(); setPlaying(false); }, []),
    onResume: useCallback(() => {}, []),
    active: !!url && showVideo,
  });

  const toggle = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="player-video">
      <div
        className="player-video__container vp-protected"
        ref={containerRef}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <ContentWatermark videoMode />
        <div className="vp-shield" />
        {loading || !showVideo ? (
          <Shimmer height="100%" />
        ) : url ? (
          <>
            <video
              ref={videoRef}
              controls
              preload="auto"
              controlsList="nodownload nofullscreen"
              disablePictureInPicture
              src={url}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onContextMenu={e => e.preventDefault()}
              onError={() => {
                const t = videoRef.current?.currentTime || 0;
                if (t > 0 && onUrlExpired) onUrlExpired(t);
              }}
            />
            {hovered && (
              <button
                className="player-video__overlay-btn"
                type="button"
                aria-label={playing ? "Pause" : "Play"}
                tabIndex={-1}
                onClick={toggle}
              >
                {playing ? <Pause size={56} /> : <Play size={56} />}
              </button>
            )}
          </>
        ) : (
          <div className="player-video__unavailable">
            <div className="player-video__unavailable-icon">
              <Play />
            </div>
            <h5>Video unavailable</h5>
            <p>This video cannot be played right now</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Helpers ───
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "";

// ─── Main Page ───
const RecordingPlayerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();
  const currentUser = getCurrentUser();

  const [recordings, setRecordings] = useState([]);
  const [currentRec, setCurrentRec] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [linkedMaterials, setLinkedMaterials] = useState([]);
  const [viewStatus, setViewStatus] = useState({}); // { videoId: { viewed, viewCount } }
  const [markingViewed, setMarkingViewed] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [courseVideoSecurity, setCourseVideoSecurity] = useState(false);

  // Desktop app required only when plan+institute enabled AND course has videoSecurity on
  const requireDesktopApp = hasPlanFeature('videoSecurityEnabled') && courseVideoSecurity && !isDesktopApp() && currentUser?.usertype === 'student';

  // ─── Session time tracking (one call on unmount / nav-away) ───
  useEffect(() => {
    if (!currentRec) return;
    const tracker = startSession({
      activityType: 'video',
      referenceId: currentRec._id,
      referenceTitle: currentRec.topic || currentRec.title || 'Recording',
    });
    return () => tracker.end();
  }, [currentRec?._id]);

  // Fetch sidebar list + view status in parallel
  useEffect(() => {
    (async () => {
      setSidebarLoading(true);
      try {
        const { data } = await apiClient.get("/videos/student/my-videos");
        const vids = data?.data || [];
        setRecordings(vids);
        if (data?.videoSecurity) setCourseVideoSecurity(true);

        // Fetch view status for the batch (single API call)
        const batchId = vids[0]?.batch?._id || vids[0]?.batch;
        if (batchId) {
          try {
            const vsRes = await apiClient.get(`/videos/batch/${batchId}/view-status`);
            setViewStatus(vsRes.data?.data || {});
          } catch {}
        }
      } catch {
        setRecordings([]);
      }
      setSidebarLoading(false);
    })();
  }, []);

  // Resolve current rec from list
  useEffect(() => {
    if (recordings.length) {
      setCurrentRec(recordings.find((r) => r._id === id) || null);
    }
  }, [recordings, id]);

  // Fetch presigned stream URL
  const fetchStreamUrl = useCallback(async (seekTime) => {
    if (!currentRec) return;
    try {
      const { data } = await apiClient.get(`/videos/${currentRec._id}/stream`);
      setVideoUrl(data?.data?.url || "");
      setShowVideo(true);
      // videoRef inside VideoPlayer will auto-load the new src
    } catch {
      setVideoUrl("");
      setShowVideo(true);
    }
  }, [currentRec]);

  useEffect(() => {
    if (!currentRec) return;
    let alive = true;
    setLoading(true);
    setVideoUrl("");
    setShowVideo(false);

    (async () => {
      try {
        const { data } = await apiClient.get(`/videos/${currentRec._id}/stream`);
        if (alive) {
          setVideoUrl(data?.data?.url || "");
          setLoading(false);
          setTimeout(() => alive && setShowVideo(true), 1500);
        }
      } catch {
        if (alive) {
          setVideoUrl("");
          setLoading(false);
          setTimeout(() => alive && setShowVideo(true), 1500);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentRec]);

  // Fetch all materials for current video (video-level + linked batch materials)
  useEffect(() => {
    if (!currentRec?._id) {
      setLinkedMaterials([]);
      return;
    }
    let alive = true;
    const batchId = currentRec.batch?._id || currentRec.batch;
    (async () => {
      try {
        // Fetch video-level materials (uploaded directly)
        const videoMatsPromise = apiClient.get(`/materials/video/${currentRec._id}`).catch(() => ({ data: { data: [] } }));
        // Fetch linked batch materials
        const linkedMatsPromise = batchId
          ? apiClient.get(`/materials/batch/${batchId}/by-video/${currentRec._id}`).catch(() => ({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } });

        const [videoRes, linkedRes] = await Promise.all([videoMatsPromise, linkedMatsPromise]);
        if (!alive) return;

        const videoMats = (videoRes.data?.data || []).filter(m => m.status === "ready");
        const linkedMats = linkedRes.data?.data || [];
        // Deduplicate by _id
        const seen = new Set(videoMats.map(m => String(m._id)));
        const merged = [...videoMats, ...linkedMats.filter(m => !seen.has(String(m._id)))];
        setLinkedMaterials(merged);
      } catch {
        if (alive) setLinkedMaterials([]);
      }
    })();
    return () => { alive = false; };
  }, [currentRec]);

  // ─── Render ───
  return (
    <>
    <div className="recordings-page">
      {/* Back link */}
      <BackButton onClick={() => navigate('/s/learning?tab=classes')} />

      <div className="player-page">
        {/* ── Main column ── */}
        <div className="player-page__main">
          {/* Player */}
          {requireDesktopApp ? (
            <DesktopAppRequired />
          ) : (
            <VideoPlayer
              url={videoUrl}
              loading={loading}
              showVideo={showVideo}
              currentRec={currentRec}
              onUrlExpired={fetchStreamUrl}
            />
          )}

          {/* Info + Materials */}
          {currentRec && (
            <div className="player-info">
              <h3 className="player-info__title">
                {currentRec.topic || currentRec.title || "Recording"}
              </h3>

              <div className="player-info__meta">
                <span>
                  <Calendar size={14} />
                  {formatDate(currentRec.recordedAt)}
                </span>
                {currentRec.duration && (
                  <span>
                    <Clock size={14} />
                    {currentRec.duration}
                  </span>
                )}
                <span>
                  <Eye size={14} />
                  {viewStatus[currentRec._id]?.viewCount ?? currentRec.viewCount ?? 0} views
                </span>
                {currentRec.fileType && (
                  <span className="player-info__topic-badge">
                    {currentRec.fileType}
                  </span>
                )}
              </div>

              {/* Mark as Viewed button */}
              <div className="player-info__actions" style={{ marginTop: 8 }}>
                {viewStatus[currentRec._id]?.viewed ? (
                  <span className="player-info__viewed-badge">
                    <CheckCircle2 size={14} /> Viewed
                  </span>
                ) : (
                  <AppButton
                    variant="outline"
                    size="sm"
                    disabled={markingViewed}
                    onClick={async () => {
                      setMarkingViewed(true);
                      try {
                        await apiClient.post(`/videos/${currentRec._id}/mark-viewed`);
                        setViewStatus((prev) => ({
                          ...prev,
                          [currentRec._id]: {
                            viewed: true,
                            viewCount: (prev[currentRec._id]?.viewCount || 0) + 1,
                          },
                        }));
                        setToast({ show: true, message: 'Marked as viewed!', type: 'success' });
                      } catch (err) {
                        console.error('Mark as viewed failed:', err);
                        setToast({ show: true, message: err?.response?.data?.message || 'Failed to mark as viewed.', type: 'error' });
                      }
                      setMarkingViewed(false);
                    }}
                  >
                    <Eye size={14} /> Mark as Viewed
                  </AppButton>
                )}
              </div>

              {currentRec.topic && currentRec.title && currentRec.topic !== currentRec.title && (
                <div className="player-info__topic-badge">
                  <Tag size={12} />
                  {currentRec.topic}
                </div>
              )}

              {currentRec.description && (
                <p className="player-info__description">{currentRec.description}</p>
              )}

              {linkedMaterials.length > 0 && (
                <div className="player-info__materials">
                  {linkedMaterials.map((mat) => (
                    <button
                      key={mat._id}
                      className="player-info__material-chip"
                      onClick={async () => {
                        try {
                          const { data } = await apiClient.get(`/materials/${mat._id}/download`);
                          if (data.data?.url) window.open(data.data.url, "_blank");
                        } catch {}
                      }}
                    >
                      <FileText size={13} />
                      <span>{mat.title}</span>
                      {mat.fileType && <span className="player-info__material-type">{mat.fileType}</span>}
                      <Download size={11} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Comments */}
          {currentRec && (
            <div className="player-comments">
              <VideoComments videoId={currentRec._id} role="student" />
            </div>
          )}
        </div>

        {/* ── Sidebar ── */}
        <div className="player-page__sidebar">
          <div className="player-sidebar">
            <div className="player-sidebar__header">
              <h5>All Recordings</h5>
              {recordings.length > 0 && <span>{recordings.length}</span>}
            </div>

            <div className="player-sidebar__list">
              {sidebarLoading ? (
                <Shimmer height={68} count={4} />
              ) : (
                recordings.map((rec) => (
                  <RecordingListItem
                    key={rec._id}
                    rec={{
                      ...rec,
                      date: rec.recordedAt,
                      thumbnail: rec.thumbnailUrl || null,
                    }}
                    active={rec._id === id}
                    viewInfo={viewStatus[rec._id]}
                    onClick={() => navigate(`/s/recordings/${rec._id}`)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <CommonToaster
      show={toast.show}
      message={toast.message}
      type={toast.type}
      onClose={() => setToast(t => ({ ...t, show: false }))}
    />
    </>
  );
};

export default RecordingPlayerPage;
